/**
 * Every law, searchable.
 *
 * Filter state lives in the query string rather than in component state, so
 * a search can be linked, bookmarked and reloaded. It also makes the back
 * button do what it looks like it does, which it does not when the filters
 * are held in memory.
 */

import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAsync, useDebounced } from "../useAsync";
import { LawCard } from "../components/LawCard";
import { Problem, Spinner } from "../components/bits";

const PER_PAGE = 24;

export function Laws() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const topic = params.get("topic") ?? "";
  const congress = params.get("congress") ?? "";
  const page = Number(params.get("page") ?? 1);

  // Typing fires a request per keystroke otherwise, and the responses do
  // not come back in the order they were sent.
  const settled = useDebounced(query);

  const laws = useAsync(
    () =>
      api.laws({
        page,
        per_page: PER_PAGE,
        query: settled || undefined,
        policy_area: topic || undefined,
        congress: congress ? Number(congress) : undefined,
      }),
    [settled, topic, congress, page],
  );
  const topics = useAsync(() => api.policyAreas(), []);

  /** Any filter change returns to page one; page 7 of a new search is empty. */
  function set(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  }

  const filtered = Boolean(query || topic || congress);

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <h1 className="font-display text-3xl font-semibold">Browse every law</h1>
      <p className="mt-1.5 text-muted">
        The 117th Congress onwards. Search matches the plain-English title, the official one, and
        the public law number.
      </p>

      <div className="mt-7 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(event) => set({ q: event.target.value })}
          type="search"
          aria-label="Search laws"
          placeholder="Search laws…"
          className="min-w-64 flex-1 rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm placeholder:text-faint"
        />
        <select
          value={topic}
          onChange={(event) => set({ topic: event.target.value })}
          aria-label="Filter by subject"
          className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
        >
          <option value="">Every subject</option>
          {(topics.data?.data ?? [])
            .filter((t) => t.laws > 0)
            .sort((a, b) => b.laws - a.laws)
            .map((t) => (
              <option key={t.policy_area} value={t.policy_area}>
                {t.policy_area} ({t.laws})
              </option>
            ))}
        </select>
        <select
          value={congress}
          onChange={(event) => set({ congress: event.target.value })}
          aria-label="Filter by Congress"
          className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
        >
          <option value="">Every Congress</option>
          <option value="119">119th (2025–26)</option>
          <option value="118">118th (2023–24)</option>
          <option value="117">117th (2021–22)</option>
        </select>
        {filtered && (
          <button
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="rounded-xl border border-line px-4 py-2.5 text-sm font-medium text-muted hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      {laws.data && (
        <p className="mt-5 text-sm text-muted" role="status">
          <strong className="tnum font-semibold text-ink">{laws.data.total.toLocaleString()}</strong>{" "}
          {laws.data.total === 1 ? "law" : "laws"}
          {filtered ? " match" : ""}
          {laws.data.pages > 1 && (
            <>
              {" "}
              · page <span className="tnum">{laws.data.page}</span> of{" "}
              <span className="tnum">{laws.data.pages}</span>
            </>
          )}
        </p>
      )}

      {laws.error && <div className="mt-6"><Problem error={laws.error} retry={laws.reload} /></div>}
      {laws.loading && !laws.data && <Spinner label="Searching…" />}

      {laws.data && laws.data.laws.length === 0 && (
        <div className="mt-8 rounded-2xl border border-line bg-card p-10 text-center">
          <p className="font-display text-xl font-semibold">Nothing matched that.</p>
          <p className="mt-2 text-muted">
            Try a broader word, or clear the filters. The corpus only holds laws from the 117th
            Congress onwards.
          </p>
        </div>
      )}

      {laws.data && laws.data.laws.length > 0 && (
        <div
          className={`mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3 ${
            laws.loading ? "opacity-60" : ""
          }`}
        >
          {laws.data.laws.map((law) => (
            <LawCard key={law.bill_id} law={law} />
          ))}
        </div>
      )}

      {laws.data && laws.data.pages > 1 && (
        <nav className="mt-10 flex items-center justify-center gap-3" aria-label="Pagination">
          <button
            disabled={page <= 1}
            onClick={() => set({ page: String(page - 1) })}
            className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
          >
            ← Previous
          </button>
          <span className="tnum text-sm text-muted">
            {laws.data.page} / {laws.data.pages}
          </span>
          <button
            disabled={page >= laws.data.pages}
            onClick={() => set({ page: String(page + 1) })}
            className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
          >
            Next →
          </button>
        </nav>
      )}
    </div>
  );
}
